import {
  clearAppTickTimer,
  clearNotificationTimer,
  fireNotificationTimer,
  loadAllNotifications,
  logout,
  removeDetailViewCard,
  setAppTickTimer,
  setPipelineAction,
  switchToOrg,
  validateCurrentSession,
} from 'js/actions';
import { isFullScreenCanvasURL } from 'js/lib/URLUtils';
import { ROUTE_FLOWS } from 'new/constants/pathnames';
import ErrorDisplay from 'js/components/elements/ErrorDisplay/ErrorDisplay';
import LoadingProgress from 'js/components/elements/LoadingProgress';
import PageVisibility from 'js/components/elements/PageVisibility/PageVisibility';
import ErrorFallback from 'js/components/elements/errors/ErrorFallback';
import ModalPipelineActionContainer from 'js/components/pipelines/ModalPipelineActionContainer';
import ProcessDisplayContainer from 'js/components/processes/ProcessDisplayContainer';
import { NEX_ORG_SWITCH_REQUEST } from 'js/constants/actionTypes';
import { MAIN_ALL } from 'js/constants/notificationsTypes';
import { ROUTE_SHOW_TOKEN, ROUTES_FOR_OAUTH } from 'js/constants/pathnames';
import { APP_TICK_TIMER_MS, AVERAGE_AUTH_RESPONSE_TIME } from 'js/constants/utilityConstants';
import AnalyticsUtils from 'js/lib/AnalyticsUtils';
import LocalStorageUtils from 'js/lib/LocalStorageUtils';
import UIUtils from 'js/lib/UIUtils';
import { getGlobalRequestStatus } from 'js/reducers/request';
import { getCurrentOrg, getCurrentUser, getOriginalAccessToken } from 'js/reducers/session';
import { includes, isEmpty } from 'lodash';
import Sidebar from 'new/components/Sidebar/Sidebar';
import { useApp } from 'new/contexts/AppContextProvider';
import { GrowthBookProvider } from 'new/contexts/GrowthBookProvider';
import { TriageChatProvider } from 'new/components/monitor/TriageChat/TriageChatProvider';
import { AlertVisibility } from 'new/elements/AlertVisibility';
import GrowthBookReadyHandler from 'new/components/GrowthBookReadyHandler';
import { SnackbarProvider } from 'new/contexts/SnackbarContext';
import React, { useCallback, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import './Main.scss';
import RootRoutes from './RootRoutes';
import Header from './components/header/Header';
import 'src/styles/generated-styles.css';
import { useExternalTokenAuth } from './hooks/useExternalTokenAuth';
import { useAppVersion } from './hooks/useAppVersion';
import useAppDispatch from 'js/hooks/useAppDispatch';

// Wraps ModalPipelineActionContainer to redirect to the flows list when the root flow is
// deleted from the full-screen canvas. onDeleteSuccess fires after the delete API resolves;
// we compare the deleted node ID against the root ID in the URL (the :id before /edit) to
// avoid redirecting on downstream nexset/sink deletes, which share the same modal.
// setPipelineAction() clears the <Prompt> block before the setTimeout navigates.
const ModalPipelineActionWithRedirect = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { pathname } = useLocation();

  const onDeleteSuccess = useCallback(
    (deletedNodeId: number) => {
      if (!isFullScreenCanvasURL(pathname)) return;
      const rootNodeId = pathname.match(/\/(\d+)\/edit$/)?.[1];
      if (!rootNodeId || String(deletedNodeId) !== rootNodeId) return;
      dispatch(setPipelineAction());
      dispatch(removeDetailViewCard());
      setTimeout(() => {
        history.push(ROUTE_FLOWS);
      }, 0);
    },
    [dispatch, history, pathname],
  );

  return <ModalPipelineActionContainer onDeleteSuccess={onDeleteSuccess} />;
};

export default function Main() {
  /*
   * Server-side session validation
   *
   * Problem: On tab visibility → visible, `onWindowFocus` can read an empty
   * `nexSession` from localStorage while Redux still holds an access token.
   * This can happen transiently (storage flush lag) or because another tab
   * cleared the session.
   *
   * Approach: When we have an access token in Redux but no persisted session
   * in localStorage, ask the server if the session is still valid via
   * GET /users/current. The CALL_PRIVATE_API middleware handles the 401 case
   * automatically (dispatches logout); a 200 means the session is fine and
   * we return without logging out.
   */
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { hideSideBar, isEmbeddedMode } = useApp();

  // Handle external token authentication for embedded mode
  useExternalTokenAuth();

  // Auto version switching based on org's GrowthBook targeting
  useAppVersion();

  const currentUser = useSelector(getCurrentUser);
  const session = useSelector((state: any) => state.session);
  const currentOrg = useSelector(getCurrentOrg);
  const userLoggedIn = !!currentUser;
  const errors = useSelector((state: any) => state.errors);
  const showSidebar =
    userLoggedIn && !hideSideBar && !includes(window.location.href, ROUTE_SHOW_TOKEN);
  const displayHeader =
    userLoggedIn &&
    !hideSideBar &&
    !includes(window.location.href, ROUTE_SHOW_TOKEN) &&
    !includes(ROUTES_FOR_OAUTH, window.location.href);
  const isLoading = useSelector(getGlobalRequestStatus);
  const isOrgSwitchLoading = useSelector((state: any) => !!state.request[NEX_ORG_SWITCH_REQUEST]);
  const accessToken = useSelector(getOriginalAccessToken);
  const expiresAt = useSelector((state: any) => state.session.expiresAt);
  useEffect(() => {
    if (userLoggedIn) {
      dispatch(loadAllNotifications(MAIN_ALL, 20, 0, 1));
    }

    dispatch(setAppTickTimer(APP_TICK_TIMER_MS));
    dispatch(fireNotificationTimer(APP_TICK_TIMER_MS));

    // this is to close the ResizeObserver error which is possibly caused by
    // react-virtuoso. taken from https://stackoverflow.com/a/76163164/406838.
    window.addEventListener('error', (e) => {
      if (e.message?.includes('ResizeObserver')) {
        const errorDisplayContainer = document.getElementById('webpack-dev-server-client-overlay');
        const errorDisplay = document.getElementById('webpack-dev-server-client-overlay-div');
        errorDisplayContainer?.setAttribute('style', 'display: none');
        errorDisplay?.setAttribute('style', 'display: none');
        const closeButton = errorDisplay?.querySelector(
          'button[aria-label="Dismiss"]',
        ) as HTMLElement;
        closeButton?.click();
        e.stopPropagation();
      }
    });

    return () => dispatch(clearAppTickTimer());
  }, []);

  const onVisibilityChange = (visible: boolean) => {
    if (!visible) {
      onWindowBlur();
      return;
    }
    onWindowFocus();
  };

  const onWindowBlur = () => {
    // stop notification timer
    dispatch(clearNotificationTimer());
  };

  const onWindowFocus = () => {
    // Embedded iframe auth is managed by the parent via postMessage. Skip
    // localStorage session checks that would spuriously logout when nexSession
    // is absent or out of sync with Redux.
    if (isEmbeddedMode) {
      dispatch(fireNotificationTimer(APP_TICK_TIMER_MS));
      return;
    }

    const now = Date.now();
    const lastOrgId = localStorage.getItem('nex_org_id');
    const nexSession = LocalStorageUtils.getObject('nexSession');
    const googleAuthLoggedIn = session.googleAuth.isLoggedIn;

    const hasAccessTokenButNoPersistedSession = !!accessToken && !nexSession;
    if (hasAccessTokenButNoPersistedSession) {
      dispatch(validateCurrentSession()).then(() => {
        dispatch(fireNotificationTimer(APP_TICK_TIMER_MS));
      });
      return;
    }

    const isMissingTokenOrSession = !accessToken || !nexSession;
    if (isMissingTokenOrSession && googleAuthLoggedIn === false) {
      // no need to call api if session has already been revoked or there is no access token.
      return dispatch(logout(false));
    }

    const sessionExpiresBeforeRefreshBuffer = expiresAt <= now + AVERAGE_AUTH_RESPONSE_TIME;
    if (sessionExpiresBeforeRefreshBuffer) {
      return dispatch(logout()); // logout and revoke access token if the session is already expired, or going to expire before the expected time for refresh response or if we dont have nexSession or token
    }

    // If another tab changed impersonation state (started, stopped, or switched to a different
    // user), reload so this tab hydrates from the updated localStorage — the same thing
    // impersonate() and endImpersonation() already do for the active tab.
    const storedImpersonatedUserId = nexSession?.impersonatedUser ?? null;
    const currentImpersonatedUserId = session.impersonatedUser?.user ?? null;
    if (storedImpersonatedUserId !== currentImpersonatedUserId) {
      window.location.reload();
      return;
    }

    const persistedOrgIdDiffersFromCurrent =
      !!lastOrgId && !isEmpty(currentOrg) && lastOrgId != currentOrg.id;
    if (persistedOrgIdDiffersFromCurrent) {
      const redirectedPath = UIUtils.getMainPage();
      history.push(redirectedPath);
      dispatch(switchToOrg(lastOrgId));
    }

    // start notification timer;
    dispatch(fireNotificationTimer(APP_TICK_TIMER_MS));
  };

  const rightContainerClass = useMemo(
    () => (isEmbeddedMode ? 'embedded-container' : 'right-container'),
    [isEmbeddedMode],
  );

  return (
    <>
      <PageVisibility onVisibilityChange={onVisibilityChange} />
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error, info) => {
          console.error(error);
          console.log(info);
          AnalyticsUtils.reportErrorToSentry(error, info);
        }}
      >
        <GrowthBookProvider>
          <SnackbarProvider>
            <GrowthBookReadyHandler />
            <TriageChatProvider>
              <div className="app-main-shell">
                <LoadingProgress isLoading={isLoading && !isOrgSwitchLoading && !isEmbeddedMode} />
                {showSidebar && <Sidebar />}
                <div className={rightContainerClass}>
                  {displayHeader && <Header />}
                  <div className="primary-content-container">
                    <RootRoutes />
                    <ModalPipelineActionWithRedirect />
                    {userLoggedIn && errors && errors.status === true && (
                      <ErrorDisplay
                        errorStatus={errors.status}
                        error={errors.error}
                        isFormError={false}
                      />
                    )}
                    <AlertVisibility />
                    <ProcessDisplayContainer />
                  </div>
                </div>
              </div>
            </TriageChatProvider>
          </SnackbarProvider>
        </GrowthBookProvider>
      </ErrorBoundary>
    </>
  );
}
